import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  getAllGroups,
  getAllRoles,
  getAllSections,
  getUserById,
  getUsersSummary,
} from "../../../../../api/api";
import { getAllFinJobTypes } from "../../../../../api/finJobTypeApi";
import {
  getAllPortalJobTypes,
  updatePortalJobType,
} from "../../../../../api/portalJobTypesApi";
import { selectStyles } from "../../../../../utils/selectStyles";
import "../CreateJobType/CreateJobType.css";
import "./UpdateJobType.css";

const TYPE_OF_FIN_JOB_TYPE_OPTIONS = [
  { value: "Проект", label: "По проекту" },
  { value: "-1", label: "Без проекта" },
  { value: "ИК", label: "ИК" },
];

const BINDING_FILTER_OPTIONS = [
  { value: "", label: "Все типы привязки" },
  ...TYPE_OF_FIN_JOB_TYPE_OPTIONS,
];

const NULL_SELECT_OPTION = { value: "", label: "Не задано" };

const mapToSelectOptions = (items, { valueKey = "id", labelKey = "name" } = {}) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => item?.[valueKey] != null && item?.[labelKey])
    .map((item) => ({
      value: item[valueKey],
      label: item[labelKey],
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));

const getBindingTypeLabel = (value) =>
  TYPE_OF_FIN_JOB_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ||
  value ||
  "—";

const getTypeOptionsForValue = (currentValue) => {
  if (!currentValue) return TYPE_OF_FIN_JOB_TYPE_OPTIONS;
  if (TYPE_OF_FIN_JOB_TYPE_OPTIONS.some((opt) => opt.value === currentValue)) {
    return TYPE_OF_FIN_JOB_TYPE_OPTIONS;
  }
  return [
    ...TYPE_OF_FIN_JOB_TYPE_OPTIONS,
    { value: currentValue, label: currentValue },
  ];
};

const findSelectOption = (options, value) => {
  if (value == null || value === "") return NULL_SELECT_OPTION;
  return options.find((opt) => String(opt.value) === String(value)) || null;
};

const getUserRolesInGroup = (user, groupId) => {
  if (!user || groupId == null) return [];

  const groupsFromDepartments = (user.departments || []).flatMap(
    (dept) => dept.groups || [],
  );
  const groups = [
    ...groupsFromDepartments,
    ...(Array.isArray(user.groups) ? user.groups : []),
  ];

  const group = groups.find((item) => Number(item.id) === Number(groupId));
  const roles = group?.roles || [];

  return roles
    .filter((role) => role?.id != null)
    .map((role) => ({
      value: role.id,
      label: role.name,
      raw: role,
    }));
};

const buildDraftFromJobType = (jobType) => ({
  name: jobType.name || "",
  is_active: Boolean(jobType.is_active),
  fin_job_type_id: jobType.fin_job_type_id ?? "",
  type_of_fin_job_type: jobType.type_of_fin_job_type || "",
  department_id: jobType.department_id ?? "",
  role_id: jobType.role_id ?? "",
  project_section_id: jobType.project_section_id ?? "",
});

const draftsEqual = (left, right) => {
  if (!left || !right) return left === right;
  return (
    (left.name || "") === (right.name || "") &&
    Boolean(left.is_active) === Boolean(right.is_active) &&
    String(left.fin_job_type_id ?? "") === String(right.fin_job_type_id ?? "") &&
    (left.type_of_fin_job_type || "") === (right.type_of_fin_job_type || "") &&
    String(left.department_id ?? "") === String(right.department_id ?? "") &&
    String(left.role_id ?? "") === String(right.role_id ?? "") &&
    String(left.project_section_id ?? "") ===
      String(right.project_section_id ?? "")
  );
};

const buildUpdatePayload = (draft, jobType) => {
  const payload = {
    name: (draft.name || jobType.name || "").trim(),
    is_active: Boolean(draft.is_active),
    type_of_fin_job_type: draft.type_of_fin_job_type || "",
    fin_job_type_id: Number(draft.fin_job_type_id || jobType.fin_job_type_id),
    role_id: Number(draft.role_id || jobType.role_id),
  };

  if (draft.department_id !== "" && draft.department_id != null) {
    payload.department_id = Number(draft.department_id);
  } else {
    payload.department_id = null;
  }

  if (draft.project_section_id !== "" && draft.project_section_id != null) {
    payload.project_section_id = Number(draft.project_section_id);
  } else {
    payload.project_section_id = null;
  }

  return payload;
};

function UpdateJobType() {
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState("");

  const [finJobTypeOptions, setFinJobTypeOptions] = useState([]);
  const [roleOptionsCatalog, setRoleOptionsCatalog] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [catalogsError, setCatalogsError] = useState("");

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState("");

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [bindingFilter, setBindingFilter] = useState(BINDING_FILTER_OPTIONS[0]);

  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");

  const [jobTypes, setJobTypes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [jobTypesLoading, setJobTypesLoading] = useState(false);
  const [jobTypesError, setJobTypesError] = useState("");

  const [savingIds, setSavingIds] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [rowSuccess, setRowSuccess] = useState({});

  const userRoleOptions = useMemo(
    () => getUserRolesInGroup(userDetails, selectedDepartment?.value),
    [userDetails, selectedDepartment],
  );

  const departmentSelectOptions = useMemo(
    () => [NULL_SELECT_OPTION, ...departments],
    [departments],
  );

  const roleSelectOptions = useMemo(
    () =>
      roleOptionsCatalog.length > 0 ? roleOptionsCatalog : userRoleOptions,
    [roleOptionsCatalog, userRoleOptions],
  );

  useEffect(() => {
    let cancelled = false;

    const loadReferenceData = async () => {
      setCatalogsLoading(true);
      setCatalogsError("");
      setDepartmentsLoading(true);
      setDepartmentsError("");

      try {
        const [groups, finJobTypes, roles, sections] = await Promise.all([
          getAllGroups(),
          getAllFinJobTypes(),
          getAllRoles(null, true),
          getAllSections(null, true),
        ]);

        if (cancelled) return;

        const departmentOptions = (Array.isArray(groups) ? groups : [])
          .filter((group) => group?.id != null && group?.name && group.is_active)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));

        setDepartments(departmentOptions);
        setFinJobTypeOptions(mapToSelectOptions(finJobTypes));
        setRoleOptionsCatalog(mapToSelectOptions(roles));
        setSectionOptions(mapToSelectOptions(sections));
      } catch (err) {
        if (!cancelled) {
          const message = err.message || "Не удалось загрузить справочники";
          setCatalogsError(message);
          setDepartmentsError(message);
        }
      } finally {
        if (!cancelled) {
          setCatalogsLoading(false);
          setDepartmentsLoading(false);
        }
      }
    };

    loadReferenceData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedDepartment?.value) {
      setEmployees([]);
      setSelectedEmployee(null);
      return;
    }

    let cancelled = false;

    const loadEmployees = async () => {
      setEmployeesLoading(true);
      setEmployeesError("");
      setSelectedEmployee(null);
      try {
        const response = await getUsersSummary({
          groupId: selectedDepartment.value,
          isActive: true,
          pageSize: 1000,
        });
        if (cancelled) return;
        const options = (response?.users || [])
          .map((user) => ({
            value: user.user_id || user.id,
            label: `${user.surname || ""} ${user.name || ""}`.trim(),
          }))
          .filter((item) => item.value != null && item.label)
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setEmployees(options);
      } catch (err) {
        if (!cancelled) {
          setEmployeesError(err.message || "Не удалось загрузить сотрудников");
          setEmployees([]);
        }
      } finally {
        if (!cancelled) {
          setEmployeesLoading(false);
        }
      }
    };

    loadEmployees();
    return () => {
      cancelled = true;
    };
  }, [selectedDepartment]);

  useEffect(() => {
    if (!selectedEmployee?.value) {
      setUserDetails(null);
      setSelectedRole(null);
      setUserDetailsError("");
      return;
    }

    let cancelled = false;

    const loadUserDetails = async () => {
      setUserDetailsLoading(true);
      setUserDetailsError("");
      setSelectedRole(null);
      try {
        const user = await getUserById(selectedEmployee.value);
        if (cancelled) return;
        if (!user) {
          throw new Error("Данные сотрудника не найдены");
        }
        setUserDetails(user);
      } catch (err) {
        if (!cancelled) {
          setUserDetailsError(
            err.message || "Не удалось загрузить данные сотрудника",
          );
          setUserDetails(null);
        }
      } finally {
        if (!cancelled) {
          setUserDetailsLoading(false);
        }
      }
    };

    loadUserDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedEmployee]);

  useEffect(() => {
    if (userRoleOptions.length === 0) {
      setSelectedRole(null);
      return;
    }
    if (userRoleOptions.length === 1) {
      setSelectedRole(userRoleOptions[0]);
      return;
    }
    setSelectedRole((current) => {
      if (!current) return null;
      return (
        userRoleOptions.find((role) => role.value === current.value) || null
      );
    });
  }, [userRoleOptions]);

  const loadJobTypes = useCallback(async () => {
    if (!selectedDepartment?.value || !selectedRole?.value) {
      setJobTypes([]);
      setDrafts({});
      return;
    }

    setJobTypesLoading(true);
    setJobTypesError("");
    setRowErrors({});
    setRowSuccess({});

    try {
      const params = {
        role_id: selectedRole.value,
      };
      if (bindingFilter?.value) {
        params.type_of_fin_job_type = bindingFilter.value;
      }

      const list = await getAllPortalJobTypes(params);
      const normalized = (Array.isArray(list) ? list : []).filter(
        (item) => item?.id != null,
      );

      setJobTypes(normalized);
      setDrafts(
        normalized.reduce((acc, item) => {
          acc[item.id] = buildDraftFromJobType(item);
          return acc;
        }, {}),
      );
    } catch (err) {
      setJobTypes([]);
      setDrafts({});
      setJobTypesError(err.message || "Не удалось загрузить виды работ");
    } finally {
      setJobTypesLoading(false);
    }
  }, [selectedDepartment, selectedRole, bindingFilter]);

  useEffect(() => {
    loadJobTypes();
  }, [loadJobTypes]);

  const handleDepartmentChange = (option) => {
    setSelectedDepartment(option);
    setUserDetails(null);
    setSelectedRole(null);
    setJobTypes([]);
    setDrafts({});
    setJobTypesError("");
    setRowErrors({});
    setRowSuccess({});
  };

  const handleEmployeeChange = (option) => {
    setSelectedEmployee(option);
    setJobTypes([]);
    setDrafts({});
    setJobTypesError("");
    setRowErrors({});
    setRowSuccess({});
  };

  const handleDraftChange = (jobTypeId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [jobTypeId]: {
        ...prev[jobTypeId],
        [field]: value,
      },
    }));
    setRowErrors((prev) => {
      if (!prev[jobTypeId]) return prev;
      const next = { ...prev };
      delete next[jobTypeId];
      return next;
    });
    setRowSuccess((prev) => {
      if (!prev[jobTypeId]) return prev;
      const next = { ...prev };
      delete next[jobTypeId];
      return next;
    });
  };

  const isRowDirty = (jobType) => {
    const draft = drafts[jobType.id];
    if (!draft) return false;
    return !draftsEqual(draft, buildDraftFromJobType(jobType));
  };

  const handleSaveRow = async (jobType) => {
    const draft = drafts[jobType.id];
    if (!draft) return;

    setSavingIds((prev) => ({ ...prev, [jobType.id]: true }));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[jobType.id];
      return next;
    });
    setRowSuccess((prev) => {
      const next = { ...prev };
      delete next[jobType.id];
      return next;
    });

    try {
      const payload = buildUpdatePayload(draft, jobType);
      const updated = await updatePortalJobType(jobType.id, payload);
      const merged =
        typeof updated === "object" && updated !== null
          ? { ...jobType, ...updated, id: jobType.id }
          : { ...jobType, ...payload, id: jobType.id };

      setJobTypes((prev) =>
        prev.map((item) => (item.id === jobType.id ? merged : item)),
      );
      setDrafts((prev) => ({
        ...prev,
        [jobType.id]: buildDraftFromJobType(merged),
      }));
      setRowSuccess((prev) => ({ ...prev, [jobType.id]: true }));
      setTimeout(() => {
        setRowSuccess((prev) => {
          if (!prev[jobType.id]) return prev;
          const next = { ...prev };
          delete next[jobType.id];
          return next;
        });
      }, 3000);
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [jobType.id]: err.message || "Не удалось сохранить",
      }));
    } finally {
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[jobType.id];
        return next;
      });
    }
  };

  const canShowJobTypes =
    selectedDepartment &&
    selectedEmployee &&
    selectedRole &&
    !userDetailsLoading &&
    !catalogsLoading;

  return (
    <div className="create-job-type update-job-type">
      <section className="create-job-type__section">
        <h2 className="create-job-type__section-title">
          Виды работ сотрудника
        </h2>
        <p className="create-job-type__section-desc">
          Выберите отдел и сотрудника, затем отредактируйте поля вида работ и
          сохраните изменения.
        </p>

        {catalogsError && (
          <p className="create-job-type__error create-job-type__error--submit">
            {catalogsError}
          </p>
        )}

        <ul className="create-job-type__fields">
          <li className="create-job-type__field">
            <label className="create-job-type__label">Отдел*</label>
            <Select
              options={departments}
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              styles={selectStyles}
              placeholder={
                departmentsLoading ? "Загрузка..." : "Выберите отдел"
              }
              isLoading={departmentsLoading}
              isDisabled={departmentsLoading || !!departmentsError}
              noOptionsMessage={() => "Отделы не найдены"}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {departmentsError && (
              <span className="create-job-type__error">{departmentsError}</span>
            )}
          </li>

          <li className="create-job-type__field">
            <label className="create-job-type__label">Сотрудник*</label>
            <Select
              options={employees}
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              styles={selectStyles}
              placeholder={
                !selectedDepartment
                  ? "Сначала выберите отдел"
                  : employeesLoading
                    ? "Загрузка..."
                    : "Выберите сотрудника"
              }
              isLoading={employeesLoading}
              isDisabled={
                !selectedDepartment || employeesLoading || !!employeesError
              }
              noOptionsMessage={() => "Сотрудники не найдены"}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {employeesError && (
              <span className="create-job-type__error">{employeesError}</span>
            )}
          </li>

          {selectedEmployee && (
            <li className="create-job-type__field">
              <label className="create-job-type__label">Роль в отделе*</label>
              <Select
                options={userRoleOptions}
                value={selectedRole}
                onChange={setSelectedRole}
                styles={selectStyles}
                placeholder={
                  userDetailsLoading
                    ? "Загрузка ролей..."
                    : userRoleOptions.length
                      ? "Выберите роль"
                      : "У сотрудника нет ролей в этом отделе"
                }
                isLoading={userDetailsLoading}
                isDisabled={
                  userDetailsLoading ||
                  !!userDetailsError ||
                  userRoleOptions.length === 0
                }
                noOptionsMessage={() => "Роли не найдены"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {userDetailsError && (
                <span className="create-job-type__error">{userDetailsError}</span>
              )}
            </li>
          )}

          {canShowJobTypes && (
            <li className="create-job-type__field">
              <label className="create-job-type__label">Тип привязки</label>
              <Select
                options={BINDING_FILTER_OPTIONS}
                value={bindingFilter}
                onChange={setBindingFilter}
                styles={selectStyles}
                isDisabled={jobTypesLoading}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </li>
          )}
        </ul>
      </section>

      {canShowJobTypes && (
        <section className="create-job-type__section update-job-type__list-section">
          <div className="update-job-type__list-header">
            <h3 className="update-job-type__list-title">Виды работ</h3>
            <button
              type="button"
              className="update-job-type__reload"
              onClick={loadJobTypes}
              disabled={jobTypesLoading}
            >
              {jobTypesLoading ? "Загрузка..." : "Обновить список"}
            </button>
          </div>

          {jobTypesError && (
            <p className="create-job-type__error create-job-type__error--submit">
              {jobTypesError}
            </p>
          )}

          {!jobTypesLoading && !jobTypesError && jobTypes.length === 0 && (
            <p className="update-job-type__empty">
              Виды работ не найдены для выбранной роли
              {bindingFilter?.value
                ? ` с типом «${getBindingTypeLabel(bindingFilter.value)}»`
                : ""}
              .
            </p>
          )}

          <div className="update-job-type__cards">
            {jobTypes.map((jobType) => {
              const draft = drafts[jobType.id] || buildDraftFromJobType(jobType);
              const dirty = isRowDirty(jobType);
              const isSaving = Boolean(savingIds[jobType.id]);
              const typeOptions = getTypeOptionsForValue(draft.type_of_fin_job_type);

              return (
                <article key={jobType.id} className="update-job-type__card">
                  <header className="update-job-type__card-header">
                    <div>
                      <h4 className="update-job-type__card-title">
                        {jobType.name}
                      </h4>
                      <p className="update-job-type__card-meta">id: {jobType.id}</p>
                    </div>
                    <label className="create-job-type__checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.is_active)}
                        onChange={(e) =>
                          handleDraftChange(
                            jobType.id,
                            "is_active",
                            e.target.checked,
                          )
                        }
                        disabled={isSaving}
                      />
                      <span>Активен</span>
                    </label>
                  </header>

                  <div className="update-job-type__card-grid">
                    <div className="update-job-type__field">
                      <label className="create-job-type__label">
                        Имя на портале
                      </label>
                      <input
                        type="text"
                        className="create-job-type__input"
                        value={draft.name}
                        onChange={(e) =>
                          handleDraftChange(jobType.id, "name", e.target.value)
                        }
                        disabled={isSaving}
                      />
                    </div>

                    <div className="update-job-type__field">
                      <label className="create-job-type__label">
                        fin_job_type
                      </label>
                      <Select
                        options={finJobTypeOptions}
                        value={findSelectOption(
                          finJobTypeOptions,
                          draft.fin_job_type_id,
                        )}
                        onChange={(option) =>
                          handleDraftChange(
                            jobType.id,
                            "fin_job_type_id",
                            option?.value ?? "",
                          )
                        }
                        styles={selectStyles}
                        isDisabled={isSaving || catalogsLoading}
                        placeholder="Выберите fin_job_type"
                        noOptionsMessage={() => "Не найдено"}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {jobType.fin_job_type_name && (
                        <span className="update-job-type__hint">
                          Текущее: {jobType.fin_job_type_name}
                        </span>
                      )}
                    </div>

                    <div className="update-job-type__field">
                      <label className="create-job-type__label">
                        Тип привязки
                      </label>
                      <Select
                        options={typeOptions}
                        value={typeOptions.find(
                          (opt) => opt.value === draft.type_of_fin_job_type,
                        )}
                        onChange={(option) =>
                          handleDraftChange(
                            jobType.id,
                            "type_of_fin_job_type",
                            option?.value || "",
                          )
                        }
                        styles={selectStyles}
                        isDisabled={isSaving}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>

                    <div className="update-job-type__field">
                      <label className="create-job-type__label">Отдел</label>
                      <Select
                        options={departmentSelectOptions}
                        value={findSelectOption(
                          departmentSelectOptions,
                          draft.department_id,
                        )}
                        onChange={(option) =>
                          handleDraftChange(
                            jobType.id,
                            "department_id",
                            option?.value ?? "",
                          )
                        }
                        styles={selectStyles}
                        isDisabled={isSaving}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {jobType.department_name && (
                        <span className="update-job-type__hint">
                          Текущее: {jobType.department_name}
                        </span>
                      )}
                    </div>

                    <div className="update-job-type__field">
                      <label className="create-job-type__label">Роль</label>
                      <Select
                        options={roleSelectOptions}
                        value={findSelectOption(roleSelectOptions, draft.role_id)}
                        onChange={(option) =>
                          handleDraftChange(
                            jobType.id,
                            "role_id",
                            option?.value ?? "",
                          )
                        }
                        styles={selectStyles}
                        isDisabled={isSaving || catalogsLoading}
                        placeholder="Выберите роль"
                        noOptionsMessage={() => "Роли не найдены"}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {jobType.role_name && (
                        <span className="update-job-type__hint">
                          Текущее: {jobType.role_name}
                        </span>
                      )}
                    </div>

                    <div className="update-job-type__field">
                      <label className="create-job-type__label">
                        Секция проекта
                      </label>
                      <Select
                        options={[NULL_SELECT_OPTION, ...sectionOptions]}
                        value={findSelectOption(
                          [NULL_SELECT_OPTION, ...sectionOptions],
                          draft.project_section_id,
                        )}
                        onChange={(option) =>
                          handleDraftChange(
                            jobType.id,
                            "project_section_id",
                            option?.value ?? "",
                          )
                        }
                        styles={selectStyles}
                        isDisabled={isSaving || catalogsLoading}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {jobType.project_section_name && (
                        <span className="update-job-type__hint">
                          Текущее: {jobType.project_section_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <footer className="update-job-type__card-footer">
                    <button
                      type="button"
                      className="update-job-type__save"
                      onClick={() => handleSaveRow(jobType)}
                      disabled={!dirty || isSaving}
                    >
                      {isSaving ? "Сохранение..." : "Сохранить"}
                    </button>
                    {rowSuccess[jobType.id] && (
                      <span className="update-job-type__row-success">
                        Сохранено
                      </span>
                    )}
                    {rowErrors[jobType.id] && (
                      <span className="create-job-type__error">
                        {rowErrors[jobType.id]}
                      </span>
                    )}
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

export default UpdateJobType;
